import {
  Box, Button, Flashbar, Form, FormField, Header, Input, Modal,
  Multiselect, Select, SpaceBetween, Table,
} from "@cloudscape-design/components";
import { useCallback, useEffect, useState } from "react";
import { getAuthToken } from "./auth";
import { apiFetch } from "./apiFetch";
import DeleteConfirmModal from "./DeleteConfirmModal";
import { useDocumentTitle } from "./useDocumentTitle";

const ROLE_OPTIONS = [
  "Project Manager", "Developer", "DevOps Engineer", "Business Analyst",
  "Product Owner", "Technical Lead", "QA Engineer", "System Administrator",
  "Security Engineer", "Data Engineer",
].map((r) => ({ label: r, value: r }));

interface StakeholderGroup { id: number; documentId: string; name: string; }
interface Stakeholder {
  id: number; documentId: string; firstName: string; lastName: string;
  email: string; department: string; role: string;
  stakeholder_groups: StakeholderGroup[];
}

export default function StakeholdersPage() {
  useDocumentTitle("Stakeholders");
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [groups, setGroups] = useState<StakeholderGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Stakeholder[]>([]);
  const [flash, setFlash] = useState<{ type: "success" | "error"; content: string } | null>(null);
  const [modalMode, setModalMode] = useState<"create" | "edit" | null>(null);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [department, setDepartment] = useState("");
  const [role, setRole] = useState("");
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const apiKey = getAuthToken();
  const selected = selectedItems[0];

  const fetchStakeholders = useCallback(() => {
    if (!apiKey) { setLoading(false); return; }
    setLoading(true);
    apiFetch(`/stakeholders?populate=stakeholder_groups`)
      .then((res) => { if (!res.ok) throw new Error(`API error: ${res.status}`); return res.json(); })
      .then((json) => setStakeholders(json.data.map((item: any) => ({
        id: item.id, documentId: item.documentId, firstName: item.firstName,
        lastName: item.lastName, email: item.email, department: item.department,
        role: item.role, stakeholder_groups: item.stakeholder_groups ?? [],
      }))))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [apiKey]);

  const fetchGroups = useCallback(() => {
    if (!apiKey) return;
    apiFetch(`/stakeholder-groups`)
      .then((res) => res.json())
      .then((json) => setGroups(json.data.map((g: any) => ({ id: g.id, documentId: g.documentId, name: g.name }))))
      .catch(() => {});
  }, [apiKey]);

  useEffect(() => { fetchStakeholders(); fetchGroups(); }, [fetchStakeholders, fetchGroups]);

  const openCreate = () => {
    setFirstName(""); setLastName(""); setEmail("");
    setDepartment(""); setRole(""); setSelectedGroups([]);
    setModalMode("create");
  };

  const openEdit = () => {
    if (!selected) return;
    setFirstName(selected.firstName); setLastName(selected.lastName);
    setEmail(selected.email); setDepartment(selected.department);
    setRole(selected.role);
    setSelectedGroups(selected.stakeholder_groups.map((g) => g.documentId));
    setModalMode("edit");
  };

  const handleSave = async () => {
    if (!apiKey) return;
    setSaving(true);
    try {
      const isEdit = modalMode === "edit";
      const url = isEdit ? `/stakeholders/${selected.documentId}` : `/stakeholders`;
      const res = await apiFetch(url, {
        method: isEdit ? "PUT" : "POST",
        body: JSON.stringify({ data: { firstName, lastName, email, department, role, stakeholder_groups: selectedGroups } }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error?.message || `API error: ${res.status}`); }
      setFlash({ type: "success", content: isEdit ? "Stakeholder updated" : "Stakeholder created" });
      setModalMode(null); setSelectedItems([]); fetchStakeholders();
    } catch (e: any) { setFlash({ type: "error", content: e.message }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!apiKey || !selected) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/stakeholders/${selected.documentId}`, {
        method: "DELETE",
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error?.message || `API error: ${res.status}`); }
      setFlash({ type: "success", content: "Stakeholder deleted" });
      setDeleteVisible(false); setSelectedItems([]); fetchStakeholders();
    } catch (e: any) { setFlash({ type: "error", content: e.message }); }
    finally { setSaving(false); }
  };

  const groupOptions = groups.map((g) => ({ label: g.name, value: g.documentId }));

  return (
    <SpaceBetween size="l">
      {flash && (
        <Flashbar items={[{ type: flash.type, content: flash.content, dismissible: true, onDismiss: () => setFlash(null) }]} />
      )}
      <Table
        variant="full-page" loading={loading} items={stakeholders} selectionType="single"
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems as Stakeholder[])}
        trackBy="documentId"
        columnDefinitions={[
          { id: "name", header: "Name", cell: (item) => `${item.firstName} ${item.lastName}` },
          { id: "email", header: "Email", cell: (item) => item.email },
          { id: "department", header: "Department", cell: (item) => item.department },
          { id: "role", header: "Role", cell: (item) => item.role },
          { id: "groups", header: "Groups", cell: (item) => item.stakeholder_groups.length },
        ]}
        header={
          <Header counter={`(${stakeholders.length})`} actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button disabled={!selected} onClick={openEdit}>Edit</Button>
              <Button disabled={!selected} onClick={() => setDeleteVisible(true)}>Delete</Button>
              <Button variant="primary" onClick={openCreate}>Create stakeholder</Button>
            </SpaceBetween>
          }>Stakeholders</Header>
        }
        empty={
          <Box textAlign="center" color={error ? "text-status-error" : "text-body-secondary"}>
            {error ? `Error: ${error}` : !apiKey ? "No API key configured. Go to Settings to add one." : "No stakeholders found"}
          </Box>
        }
      />
      <Modal visible={modalMode !== null} onDismiss={() => setModalMode(null)}
        header={modalMode === "edit" ? "Edit stakeholder" : "Create stakeholder"}
        footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setModalMode(null)}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>Submit</Button>
        </SpaceBetween></Box>}>
        <Form><SpaceBetween size="m">
          <FormField label="First Name"><Input value={firstName} onChange={({ detail }) => setFirstName(detail.value)} /></FormField>
          <FormField label="Last Name"><Input value={lastName} onChange={({ detail }) => setLastName(detail.value)} /></FormField>
          <FormField label="Email"><Input value={email} onChange={({ detail }) => setEmail(detail.value)} inputMode="email" /></FormField>
          <FormField label="Department"><Input value={department} onChange={({ detail }) => setDepartment(detail.value)} /></FormField>
          <FormField label="Role">
            <Select selectedOption={role ? { label: role, value: role } : null}
              onChange={({ detail }) => setRole(detail.selectedOption.value ?? "")}
              options={ROLE_OPTIONS} placeholder="Select a role" />
          </FormField>
          <FormField label="Stakeholder Groups">
            <Multiselect selectedOptions={groupOptions.filter((o) => selectedGroups.includes(o.value))}
              onChange={({ detail }) => setSelectedGroups(detail.selectedOptions.map((o) => o.value!))}
              options={groupOptions} placeholder="Select groups" />
          </FormField>
        </SpaceBetween></Form>
      </Modal>
      <DeleteConfirmModal
        visible={deleteVisible}
        header="Delete stakeholder"
        itemName={selected ? ` ` : undefined}
        loading={saving}
        onDismiss={() => setDeleteVisible(false)}
        onConfirm={handleDelete}
      />
    </SpaceBetween>
  );
}
